import {
  facilitiesLabels,
  FacilityKey,
  getLocationName,
  primaryFacilities,
} from '@koolumbus/shared/utils';
import { MaterialIcon, MaterialIconName } from '@koolumbus/web/ui';
import { createTxs, useLocale } from '@koolumbus/web/utils';
import { Location } from '@prisma/client';
import clsx from 'clsx';
import Head from 'next/head';
import React from 'react';
import { PropertyData } from '../../../lib/models';
import type { PageWithLayout } from '../../../lib/types/page';
import FavoriteButton from '../../common/FavoriteButton';
import ShareButton from '../../common/ShareButton';
import PropertyFloatingCard from './floating-card/PropertyFloatingCard';
import PropertyDetailsGrid from './PropertyDetailsGrid';
import PropertyImagesGrid from './PropertyImagesGrid';
import PropertyLocationSection from './PropertyLocationSection';

export interface PropertyPageProps {
  property: PropertyData;
}

const PropertyPage: PageWithLayout<PropertyPageProps> = ({ property }) => {
  return (
    <>
      <Head>
        <title>{`${property.name} | Koolumbus`}</title>
      </Head>

      <div className="max-w-7xl mx-auto flex flex-col items-center px-0 md:px-4 lg:px-8">
        <div className="mx-auto w-full pt-5 md:pt-12">
          <PageHeader property={property} />

          <PropertyImagesGrid property={property} />

          <div className="flex w-full justify-between mt-[30px]">
            <div className="flex flex-col w-full lg:w-auto pb-24">
              <div className="flex flex-col px-6 md:px-0 space-y-8 mb-20 items-start">
                <DescriptionSection property={property} />
                <AmenitiesSection property={property} />
              </div>

              <PropertyLocationSection property={property} />
            </div>

            <PropertyFloatingCard property={property} />
          </div>
        </div>
      </div>
    </>
  );
};

const PageHeader: React.FC<React.PropsWithChildren<{ property: PropertyData }>> = ({
  property,
}) => {
  const tx = useTxs();

  return (
    <div className={clsx('flex justify-between items-center mb-4 px-4 sm:px-6', 'md:mb-8 md:px-1')}>
      <div className="flex flex-col">
        <p className="font-semibold sm:font-bold text-xl md:text-2xl">{property.name}</p>
        <p className="text-gray-400 text-sm">{tx.header.subtitle(property.location)}</p>
      </div>

      <div className="flex items-center space-x-3">
        <ShareButton propertyId={property.id} />
        <FavoriteButton propertyId={property.id} />
      </div>
    </div>
  );
};

const DescriptionSection: React.FC<React.PropsWithChildren<{ property: PropertyData }>> = ({
  property,
}) => {
  const tx = useTxs();

  return (
    <div className="flex flex-col">
      <p className="text-gray-500">{property.address}</p>

      <h5 className="text-gray-800 text-lg font-medium mt-5 sm:mt-7">{tx.description}</h5>
      <p className="text-gray-700 mt-3">{property.description}</p>

      <PropertyDetailsGrid property={property} />
    </div>
  );
};

const AmenitiesSection: React.FC<React.PropsWithChildren<{ property: PropertyData }>> = ({
  property,
}) => {
  const locale = useLocale();
  const tx = useTxs();

  const items = primaryFacilities.filter((value) => property.facilities?.[value]);

  const getIcon = (value: FacilityKey): MaterialIconName => {
    switch (value) {
      case 'washingMachine':
        return 'local-laundry-service';
      case 'wirelessInternet':
        return 'wifi';
      case 'heating':
        return 'water';
      case 'dishwasher':
        return 'dining';
      case 'airConditioning':
        return 'ac-unit';
      default:
        return 'lock-open';
    }
  };

  return (
    <div className="flex flex-col w-full">
      <p className="mb-6 text-lg font-medium text-gray-800">{tx.amenities}</p>

      <div className="grid gap-4 sm:gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {items.map((item, i) => (
          <div className="flex-center flex-col shadow-sm border rounded-md py-5 bg-white" key={i}>
            <MaterialIcon
              variant="outlined"
              icon={getIcon(item)}
              className="text-4xl text-gray-500 mb-2"
            />
            <p className="text-gray-500">{facilitiesLabels[item][locale]}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const useTxs = createTxs({
  en: {
    header: {
      subtitle: (location: Location) => `Apartment in ${getLocationName(location, 'en')}`,
    },
    description: 'Description',
    amenities: 'Amenities',
  },
  it: {
    header: {
      subtitle: (location: Location) => `Appartamento a ${getLocationName(location, 'it')}`,
    },
    description: 'Descrizione',
    amenities: 'Caratteristiche',
  },
});

export default PropertyPage;
